import type { OperatorRigSink, OperatorRigState, Point3, RigPart, RigSurface } from "@common/utils/operatorRig";
import type { Vector } from "@common/utils/vector";
import { operatorSkin } from "@common/definitions/operatorSkins";
import { operatorFacePaint } from "./operatorPaint";
import { RIG_IDENTITY, rigLight, rigProjection, type RigTransform } from "@common/utils/operatorKinematics";

const point = (): Point3 => ({ x: 0, y: 0, z: 0 });
const tubeFaces: number[][] = [[7, 6, 5, 4, 3, 2, 1, 0], [8, 9, 10, 11, 12, 13, 14, 15]];
for (let i = 0; i < 8; i++) tubeFaces.push([i, (i + 1) % 8, (i + 1) % 8 + 8, i + 8]);
const circle = Array.from({ length: 8 }, (_, i) => [Math.cos(i * Math.PI / 4), Math.sin(i * Math.PI / 4)]);

interface Edge { a: number; b: number; faces: number[]; quad: Vector[]; }
type RenderSurface = RigSurface & { depths: number[]; lights: number[]; ink: Vector[][]; };
interface Mesh {
    vertices: Point3[];
    faces: number[][];
    normals?: Point3[];
    faceNormals?: Point3[];
}
interface PartCache {
    part: RigPart;
    points: Vector[];
    depths: Float64Array;
    lights: Float64Array;
    visible: Uint8Array;
    surfaces: RenderSurface[];
    edges: Edge[];
    faces: number[][];
    vertices: Point3[];
    parameters: number[];
    template?: Mesh;
    paint?: Uint32Array;
    paintColor?: number;
    paintAccent?: number;
}

/** Per-actor mutable render workspace. Its output lives until this actor's next draw.
 * The recipe, projected silhouette, depth and inset ink match buildOperatorRig.
 * No pose/yaw quantisation, animation decimation, hitbox changes or global pose cache.
 */
export class RetainedOperatorRig implements OperatorRigSink {
    private readonly cache = new Map<string, PartCache>();
    private readonly parts: RigPart[] = [];
    private cy = 1;
    private sy = 0;
    private accent = 0;
    private projection = rigProjection(1, 0);
    private light = rigLight(1, 0, RIG_IDENTITY);
    constructor(private readonly painted = false) {}

    begin(state: OperatorRigState): RigPart[] {
        const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
        this.cy = Math.cos(yaw); this.sy = Math.sin(yaw);
        this.setTransform(RIG_IDENTITY);
        if (this.painted) this.accent = operatorSkin(state.skinId).accent;
        this.parts.length = 0;
        return this.parts;
    }

    setTransform(transform: RigTransform): void {
        this.projection = rigProjection(this.cy, this.sy, transform);
        this.light = rigLight(this.cy, this.sy, transform);
    }

    private entry(name: string, vertices: number, faces: number[][], convex: boolean): PartCache {
        let item = this.cache.get(name);
        if (item && item.points.length === vertices && item.faces === faces) return item;
        const points = Array.from({ length: vertices }, () => ({ x: 0, y: 0 }));
        const surfaces = faces.map(face => ({
            points: face.map(i => points[i]), depth: 0, color: 0,
            depths: face.map(() => 0), lights: face.map(() => 0), ink: []
        } as RenderSurface));
        const edgeMap = new Map<number, Edge>();
        for (let f = 0; f < faces.length; f++) {
            const face = faces[f];
            for (let i = 0; i < face.length; i++) {
                const a = face[i], b = face[(i + 1) % face.length];
                const key = Math.min(a, b) * vertices + Math.max(a, b);
                let edge = edgeMap.get(key);
                if (!edge) {
                    edge = { a, b, faces: [], quad: [points[a], points[b], { x: 0, y: 0 }, { x: 0, y: 0 }] };
                    edgeMap.set(key, edge);
                }
                edge.faces.push(f);
            }
        }
        item = {
            part: { name, hull: [], surfaces: [], convex }, points, surfaces,
            depths: new Float64Array(vertices), lights: new Float64Array(vertices), visible: new Uint8Array(faces.length),
            edges: [...edgeMap.values()], faces, vertices: Array.from({ length: vertices }, point), parameters: []
        };
        this.cache.set(name, item);
        return item;
    }

    private project(item: PartCache, vertices: Point3[], color: number, ink: boolean, normals?: Point3[], faceNormals?: Point3[]): void {
        const m = this.projection, light = this.light;
        const { points, depths, lights, visible, faces, surfaces, part } = item;
        if (this.painted && (item.paintColor !== color || item.paintAccent !== this.accent)) {
            item.paint = operatorFacePaint(part.name, item.template?.vertices ?? vertices, faces, color, this.accent);
            item.paintColor = color; item.paintAccent = this.accent;
        }
        part.surfaces.length = 0;
        for (let i = 0; i < vertices.length; i++) {
            const v = vertices[i], q = points[i];
            q.x = m[0] * v.x + m[1] * v.y + m[2] * v.z + m[3];
            q.y = m[4] * v.x + m[5] * v.y + m[6] * v.z + m[7];
            depths[i] = m[8] * v.x + m[9] * v.y + m[10] * v.z + m[11];
            if (normals) {
                const n = normals[i];
                lights[i] = (n.x * light.x + n.y * light.y + n.z * light.z)
                    / (Math.hypot(n.x, n.y, n.z) || 1);
            }
        }
        for (let f = 0; f < faces.length; f++) {
            const face = faces[f], surface = surfaces[f];
            surface.ink.length = 0;
            const a = points[face[0]], b = points[face[1]], c = points[face[2]];
            if ((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x) <= 0.000001) { visible[f] = 0; continue; }
            visible[f] = 1;
            let nx: number, ny: number, nz: number;
            if (faceNormals) {
                ({ x: nx, y: ny, z: nz } = faceNormals[f]);
            } else {
                const va = vertices[face[0]], vb = vertices[face[1]], vc = vertices[face[2]];
                const ux = vb.x - va.x, uy = vb.y - va.y, uz = vb.z - va.z;
                const vx = vc.x - va.x, vy = vc.y - va.y, vz = vc.z - va.z;
                nx = uy * vz - uz * vy; ny = uz * vx - ux * vz; nz = ux * vy - uy * vx;
            }
            const dot = (nx * light.x + ny * light.y + nz * light.z) / (Math.hypot(nx, ny, nz) || 1);
            const tone = dot < 0.08 ? 0.63 : dot < 0.65 ? 0.88 : 1.12;
            surface.color = (Math.min(255, Math.round((color >> 16 & 255) * tone)) << 16)
                | (Math.min(255, Math.round((color >> 8 & 255) * tone)) << 8) | Math.min(255, Math.round((color & 255) * tone));
            surface.pigment = item.paint?.[f] ?? color;
            let sum = 0;
            for (let i = 0; i < face.length; i++) {
                const index = face[i];
                surface.depths[i] = depths[index];
                surface.lights[i] = normals ? lights[index] : dot;
                sum += depths[index];
            }
            surface.depth = sum / face.length;
            part.surfaces.push(surface);
        }
        if (ink) for (const edge of item.edges) {
            let count = 0, owner = 0;
            for (const face of edge.faces) if (visible[face]) { count++; owner = face; }
            if (count !== 1) continue;
            const face = faces[owner], surface = surfaces[owner];
            // Match the boundary direction in the visible face, not the hidden neighbour.
            let a = edge.a, b = edge.b;
            for (let i = 0; i < face.length; i++) if (face[i] === edge.b && face[(i + 1) % face.length] === edge.a) {
                a = edge.b; b = edge.a; break;
            }
            const pa = points[a], pb = points[b];
            if (Math.hypot(pb.x - pa.x, pb.y - pa.y) < 0.002) continue;
            let cx = 0, cy = 0;
            for (const index of face) { cx += points[index].x; cy += points[index].y; }
            cx /= face.length; cy /= face.length;
            edge.quad[0] = pa; edge.quad[1] = pb;
            for (let i = 2; i < 4; i++) {
                const source = i === 2 ? pb : pa, target = edge.quad[i];
                const t = Math.min(0.5, 0.045 / (Math.hypot(cx - source.x, cy - source.y) || 1));
                target.x = source.x + (cx - source.x) * t; target.y = source.y + (cy - source.y) * t;
            }
            surface.ink.push(edge.quad);
        }
        this.parts.push(part);
    }

    mesh(name: string, vertices: Point3[], faces: number[][], color: number, ink: boolean, normals: Point3[] | undefined, convex: boolean): void {
        const previous = this.cache.get(name);
        // Custom patches are small. Keep their arrays but invalidate if topology changes.
        if (previous && faces.length === previous.faces.length && faces.every((f, i) =>
            f.length === previous.faces[i].length && f.every((v, j) => v === previous.faces[i][j]))) faces = previous.faces;
        this.project(this.entry(name, vertices.length, faces, convex), vertices, color, ink, normals);
    }

    private template(name: string, parameters: number[], make: () => Mesh): PartCache {
        const previous = this.cache.get(name);
        if (previous?.template && parameters.length === previous.parameters.length && parameters.every((n, i) => n === previous.parameters[i])) return previous;
        const mesh = make();
        mesh.faceNormals = mesh.faces.map(face => {
            const a = mesh.vertices[face[0]], b = mesh.vertices[face[1]], c = mesh.vertices[face[2]];
            const ux = b.x - a.x, uy = b.y - a.y, uz = b.z - a.z;
            const vx = c.x - a.x, vy = c.y - a.y, vz = c.z - a.z;
            return { x: uy * vz - uz * vy, y: uz * vx - ux * vz, z: ux * vy - uy * vx };
        });
        const item = this.entry(name, mesh.vertices.length, mesh.faces, true);
        item.template = mesh; item.parameters = parameters;
        return item;
    }

    private translated(item: PartCache, center: Point3, color: number): void {
        const template = item.template;
        if (!template) throw new Error("Missing operator primitive template");
        for (let i = 0; i < template.vertices.length; i++) {
            const a = template.vertices[i], b = item.vertices[i];
            b.x = center.x + a.x; b.y = center.y + a.y; b.z = center.z + a.z;
        }
        this.project(item, item.vertices, color, true, template.normals, template.faceNormals);
    }

    ellipsoid(name: string, center: Point3, size: Point3, color: number, segments: number, rings: number, phiEnd: number): void {
        const item = this.template(name, [size.x, size.y, size.z, segments, rings, phiEnd], () => {
            const vertices: Point3[] = [], faces: number[][] = [];
            for (let r = 0; r <= rings; r++) for (let i = 0; i < segments; i++) {
                const phi = phiEnd * r / rings, theta = 2 * Math.PI * i / segments;
                vertices.push({ x: size.x * Math.sin(phi) * Math.cos(theta), y: size.y * Math.sin(phi) * Math.sin(theta), z: size.z * Math.cos(phi) });
            }
            for (let r = 0; r < rings; r++) for (let i = 0; i < segments; i++) {
                const j = (i + 1) % segments;
                const a = r * segments + i, b = r * segments + j, c = (r + 1) * segments + j, d = (r + 1) * segments + i;
                faces.push(r === 0 ? [a, c, d] : r === rings - 1 && phiEnd === Math.PI ? [a, b, c] : [a, b, c, d]);
            }
            if (phiEnd !== Math.PI) faces.push(Array.from({ length: segments }, (_, i) => rings * segments + i));
            return {
                vertices,
                faces: faces.map(f => f.reverse().map(i => i < segments ? 0 : phiEnd === Math.PI && i >= rings * segments ? rings * segments : i)),
                normals: vertices.map(v => ({ x: v.x / size.x ** 2, y: v.y / size.y ** 2, z: v.z / size.z ** 2 }))
            };
        });
        this.translated(item, center, color);
    }

    box(name: string, center: Point3, size: Point3, color: number, bevel: number): void {
        const item = this.template(name, [size.x, size.y, size.z, bevel], () => {
            const oct = [[-1, -1 + bevel], [-1 + bevel, -1], [1 - bevel, -1], [1, -1 + bevel], [1, 1 - bevel], [1 - bevel, 1], [-1 + bevel, 1], [-1, 1 - bevel]];
            return { faces: tubeFaces, vertices: [-1, 1].flatMap(z => oct.map(([x, y]) => ({ x: x * size.x, y: y * size.y, z: z * size.z }))) };
        });
        this.translated(item, center, color);
    }

    limb(name: string, a: Point3, b: Point3, radius: number, color: number): void {
        const item = this.entry(name, 16, tubeFaces, true);
        let dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        const length = Math.hypot(dx, dy, dz) || 1;
        dx /= length; dy /= length; dz /= length;
        let ux = -dy, uy = dx, ul = Math.hypot(ux, uy);
        if (ul < 0.001) { ux = 1; uy = 0; ul = 1; }
        ux /= ul; uy /= ul;
        const vx = -dz * uy, vy = dz * ux, vz = dx * uy - dy * ux;
        for (let end = 0; end < 2; end++) for (let i = 0; i < 8; i++) {
            const center = end ? b : a, v = item.vertices[end * 8 + i], [cos, sin] = circle[i];
            v.x = center.x + radius * (ux * cos + vx * sin);
            v.y = center.y + radius * (uy * cos + vy * sin);
            v.z = center.z + radius * vz * sin;
        }
        this.project(item, item.vertices, color, true);
    }
}
